'use client'

import {useEffect, useRef, useState} from 'react'
import {tv, type VariantProps} from 'tailwind-variants'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/design-system/foundations/Table'
import {HgTableCell, type HgTableCellType} from '~/design-system/hg/components'

export type StandardTableRow = [
  HgTableCellType,
  HgTableCellType,
  HgTableCellType?,
  HgTableCellType?,
  HgTableCellType?,
  HgTableCellType?,
]

type StandardTableVariantProps = VariantProps<typeof standardTableVariants>

export type StandardTableProps = {
  columnTitles?: StandardTableRow | null
  rows: StandardTableRow[]
  className?: string
} & StandardTableVariantProps

export const standardTableVariants = tv({
  slots: {
    wrapper:
      'relative border-border-default after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-0 after:w-[200px] after:bg-gradient-to-l after:from-background-default after:transition-opacity data-[scroll=false]:after:opacity-0 ',
    cellContent: 'min-w-[156px] py-s3 pr-s3',
    tableRow:
      'grid grid-flow-col grid-cols-[repeat(auto-fit,minmax(156px,1fr))] gap-x-grid-column-gap',
  },
  variants: {
    lastColumnAlignment: {
      left: '',
      right: {
        wrapper:
          '[&_td:last-of-type>*]:justify-end [&_td:last-of-type]:text-right [&_th:last-of-type>*]:justify-end [&_th:last-of-type]:text-right',
      },
    },
    numColumns: {
      2: '',
      3: '',
      4: '',
      5: '',
      6: '',
    },
    asymmetrical: {
      true: '',
    },
  },
  compoundVariants: [
    {
      numColumns: 2,
      asymmetrical: true,
      class: {tableRow: 'grid-cols-[2fr_1fr] md:grid-cols-[5fr_3fr]'},
    },
    {
      numColumns: 3,
      asymmetrical: true,
      class: {tableRow: 'grid-cols-[3fr_2fr_2fr] md:grid-cols-[2fr_1fr_1fr]'},
    },
    {
      numColumns: 4,
      asymmetrical: true,
      class: {
        tableRow: 'grid-cols-[3fr_2fr_2fr_2fr] md:grid-cols-[3fr_1fr_1fr_1fr]',
      },
    },
  ],
})

export default function StandardTable({
  rows,
  columnTitles,
  lastColumnAlignment,
  asymmetrical = false,
  className,
}: StandardTableProps) {
  const lastColumnRef = useRef<HTMLTableCellElement | null>(null)
  const wrapperRef = useRef<HTMLTableElement | null>(null)
  const [hasScroll, setHasScroll] = useState(false)

  const {wrapper, cellContent, tableRow} = standardTableVariants({
    lastColumnAlignment,
    numColumns: rows[0]?.length,
    asymmetrical,
  })

  useEffect(() => {
    if (!lastColumnRef.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHasScroll(!entry.isIntersecting)
      },
      {threshold: 0.6, root: wrapperRef.current}
    )
    observer.observe(lastColumnRef.current)
    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div
      className={wrapper({class: className})}
      data-scroll={hasScroll}
      ref={wrapperRef}
    >
      <Table>
        <TableHeader className="text-text-emphasized arcadia-heading-9">
          {columnTitles && (
            <TableRow className={tableRow()}>
              {columnTitles.map(
                (column, index) =>
                  column && (
                    <TableHead key={index} className={cellContent()}>
                      <HgTableCell {...column} className="items-start" />
                    </TableHead>
                  )
              )}
            </TableRow>
          )}
        </TableHeader>
        <TableBody className="text-text-default arcadia-body-2">
          {rows.map((column, rowIndex) => (
            <TableRow key={rowIndex} className={tableRow()}>
              {column.map(
                (cell, columnIndex) =>
                  cell && (
                    <TableCell
                      key={columnIndex}
                      className={cellContent()}
                      ref={
                        columnIndex === column.length - 1 &&
                        rowIndex === rows.length - 1
                          ? lastColumnRef
                          : null
                      }
                    >
                      <HgTableCell {...cell} className="items-start" />
                    </TableCell>
                  )
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
